import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {Box, Grid, Stack, Typography} from "@mui/material";
import {Add, Remove} from "@mui/icons-material";
import {Descendant} from "slate";
import Page from "../Components/Page";
import Card from "../Components/Card";
import IconButtonPop from "../Components/IconButtonPop";
import ArticleCard from "../Components/ArticleCard";
import RichText from "../Components/RichText";
import ArticleBody from "../Components/ArticleBody";
import FirebaseQuery from "../Util/firebaseQuery";
import {where} from "firebase/firestore";
import {getDownloadURL, ref} from "firebase/storage";
import {storage} from "../Util/firebaseConfig";
import {useArticles} from "../Util/useArticles";
import {SectionType, Subject, subjectColours} from "../Util/constants";
import {Article} from "./Dashboard";
import {Keyword, RichTextNode, ScimosArticle} from "../Util/types";
import {camelCaseToNormal, capitalise, capitaliseWords, convertDate, serialiseSlate} from "../Util/util";

type LegacyArticle = Article & {id: string, url?: string, keywords?: unknown};

// Legacy articles live in a flat "articles" collection (Slate or RichTextNode
// body). Newer ones are produced by tools/scimos_convert.py and live in
// learn / news / book-reviews, keyed the same way but with a different block
// schema (see ArticleBody). We don't know which collection an id belongs to
// up front, so we probe each in turn and remember which shape we found.
type LoadedArticle =
    | {schema: "legacy", data: LegacyArticle}
    | {schema: "block", data: ScimosArticle};

const NEW_COLLECTIONS = ["learn", "news", "book-reviews"] as const;
const FALLBACK_COLOUR = "#666666";

const MIN_FONT = 14;
const MAX_FONT = 26;

// Older articles store their body as Slate nodes ({type, children}); newer ones
// use the lightweight RichTextNode format ({type, content}). Detect Slate so we
// render each with the right renderer.
function isSlate(body: unknown): body is Descendant[] {
    return Array.isArray(body) && body.length > 0 && typeof body[0] === "object" && body[0] !== null && "children" in (body[0] as object);
}

// Normalises stored keyword records (which may be {keyword, definition}) into the
// Keyword shape RichText expects.
function toKeywords(raw: unknown): Keyword[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((k: {keyword?: string, definition?: string, description?: RichTextNode[], keywords?: string[]}) => ({
            keyword: k.keyword ?? "",
            description: Array.isArray(k.description) ? k.description : [{type: "p", content: k.definition ?? ""}],
            keywords: k.keywords ?? [],
        }))
        .filter((k) => k.keyword);
}

export default function ArticlePage() {
    const {id} = useParams();
    const [article, setArticle] = useState<LoadedArticle | null>();
    const [blockKeywords, setBlockKeywords] = useState<Keyword[]>([]);
    const [fontSize, setFontSize] = useState(18);

    useEffect(() => {
        if (!id) return;
        let active = true;
        const firebaseQuery = new FirebaseQuery();

        (async () => {
            try {
                const data = await firebaseQuery.getDocData(["articles", id]);
                const url = await getDownloadURL(ref(storage, `/articles/${id}.png`)).catch(() => undefined);
                if (active) setArticle({schema: "legacy", data: {...(data as Article), id, url}});
                return;
            } catch {
                // not in the legacy collection — fall through to the newer per-type ones
            }
            for (const collection of NEW_COLLECTIONS) {
                try {
                    const data = await firebaseQuery.getDocData([collection, id]);
                    if (active) setArticle({schema: "block", data: data as ScimosArticle});
                    return;
                } catch {
                    // try the next collection
                }
            }
            if (active) setArticle(null);
        })();

        return () => {
            active = false;
        };
    }, [id]);

    // Block-schema articles (learn/news/book-reviews) only store keyword titles
    // inline; the definitions to tag/pop up live in the "keywords" collection.
    useEffect(() => {
        if (article?.schema !== "block" || !article.data.keywords?.length) {
            setBlockKeywords([]);
            return;
        }
        let active = true;
        const firebaseQuery = new FirebaseQuery();
        const terms = article.data.keywords;

        (async () => {
            const chunks: string[][] = [];
            for (let i = 0; i < terms.length; i += 10) chunks.push(terms.slice(i, i + 10));
            const results = await Promise.all(
                chunks.map((chunk) => firebaseQuery.getDocsWhere(["keywords"], where("keyword", "in", chunk))),
            );
            if (active) setBlockKeywords(toKeywords(Object.values(Object.assign({}, ...results))));
        })();

        return () => {
            active = false;
        };
    }, [article]);

    // Recommended articles from the same section, loaded once the type is known.
    // Book reviews have no equivalent section to recommend from.
    const sectionType = article?.schema === "legacy" ? (article.data.type as SectionType) :
        article?.schema === "block" && article.data.type !== "book-review" ? article.data.type : undefined;
    const recommended = useArticles(sectionType);

    if (article === undefined) return null;
    if (article === null) {
        return <Page title={"Article not found"} back><Typography sx={{opacity: 0.7}}>We couldn&apos;t find that article.</Typography></Page>;
    }

    const isLegacy = article.schema === "legacy";
    const colour = isLegacy ?
        subjectColours[article.data.subject] :
        subjectColours[article.data.subject.toLowerCase() as Subject] ?? FALLBACK_COLOUR;
    const author = capitaliseWords(camelCaseToNormal(article.data.author ?? ""));
    const date = isLegacy ? convertDate(article.data.date, "visual") as string : undefined;
    const breadcrumb = isLegacy ?
        [capitalise(article.data.subject), article.data.topic, article.data.subtopic].filter(Boolean).join(" > ") :
        [capitalise(article.data.subject), article.data.topic, ...article.data.subtopics].filter(Boolean).join(" > ");
    const introText = isLegacy ? article.data.mainSummary : article.data.summary;
    const keywords = isLegacy ? toKeywords(article.data.keywords) : blockKeywords;
    const recs = (recommended ?? []).filter((a) => a.id !== article.data.id).slice(0, 5);

    return (
        <Page metaTitle={`${article.data.title} | Scimos`} metaDesc={article.data.summary ?? undefined} grid>
            {/* Article head — full width */}
            <Grid item xs={12}>
                <Card sx={{borderTop: `7px solid ${colour}`}} contentSx={{p: 3}}>
                    <Stack direction={{xs: "column", sm: "row"}} spacing={3} justifyContent={"space-between"} alignItems={{xs: "flex-start", sm: "center"}}>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems={{xs: "flex-start", sm: "center"}}>
                            {isLegacy && article.data.url && <Box component={"img"} src={article.data.url} width={"96px"} height={"96px"} sx={{borderRadius: "50%", flexShrink: 0}}/>}
                            <Stack spacing={0.5}>
                                <Typography variant={"h3"}>{article.data.title}</Typography>
                                {breadcrumb && <Typography variant={"subtitle1"} sx={{color: colour, fontWeight: 600}}>{breadcrumb}</Typography>}
                                <Typography variant={"body2"} sx={{opacity: 0.7}}>{author}{date ? ` • ${date}` : ""}</Typography>
                            </Stack>
                        </Stack>
                        <Stack direction={"row"} spacing={1} sx={{flexShrink: 0}}>
                            <IconButtonPop responsive={false} title={"Decrease text size"} onClick={() => setFontSize((f) => Math.max(MIN_FONT, f - 2))}><Remove/></IconButtonPop>
                            <IconButtonPop responsive={false} title={"Increase text size"} onClick={() => setFontSize((f) => Math.min(MAX_FONT, f + 2))}><Add/></IconButtonPop>
                        </Stack>
                    </Stack>
                    {introText && <Typography sx={{mt: 2, fontStyle: "italic", opacity: 0.85}}>{introText}</Typography>}
                </Card>
            </Grid>

            {/* Body — md 8, full width on sm/xs */}
            <Grid item xs={12} md={8}>
                <Box sx={{"fontSize": `${fontSize}px`, "lineHeight": 1.6, "& img": {maxWidth: "100%"}}}>
                    {article.schema === "legacy" ?
                        (isSlate(article.data.body) ?
                            serialiseSlate(article.data.body) :
                            <RichText content={article.data.body as unknown as RichTextNode[]} keywords={keywords} color={colour}/>) :
                        <ArticleBody blocks={article.data.body} color={colour} keywords={keywords}/>}
                </Box>
            </Grid>

            {/* Sidebar — md 4, full width on sm/xs */}
            <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                    <Typography variant={"h5"} sx={{borderBottom: `2px solid ${colour}`, pb: 0.5}}>Recommended</Typography>
                    {recs.length === 0 && <Typography variant={"body2"} sx={{opacity: 0.6}}>Nothing here yet.</Typography>}
                    {recs.map((a) => <ArticleCard key={a.id} article={a} variant={"compact"}/>)}
                    {false && <Card sx={{background: colour, color: "white"}} contentSx={{p: 2}}>
                        <Stack spacing={0.5}>
                            <Typography variant={"h6"} color={"white"}>Get involved</Typography>
                            <Typography variant={"body2"} color={"white"}>Want to write for Scimos? Feature your day in the life or share your research.</Typography>
                        </Stack>
                    </Card>}
                </Stack>
            </Grid>
        </Page>
    );
}
